import { useFormContext } from "react-hook-form";
import { z } from "zod";
import { useEffect, useRef, useState } from "react";
import { FormField } from "@/components/ui/FormField";
import Dropdown from "@/components/ui/Dropdown";
import { getMasterDropdownService, getCitiesBasedOnState } from "@/services/masters";

export const branchSchema = z.object({
  Branch_Name: z.string().min(2, "Branch name must be at least 2 characters"),
  State: z.string().min(1, "Please select a state"),
  State_Code: z.string().optional(),
  City: z.string().min(1, "Please select a district"),
  City_Code: z.string().optional(),
  Zone: z.string().min(1, "Please select a zone"),
  Zone_Code: z.string().optional(),
  Branch_Address: z.string().min(5, "Branch address must be at least 5 characters"),
});

export type BranchFormData = z.infer<typeof branchSchema>;

interface AddBranchFormProps {
  pendingCity?: string;
}

const AddBranchForm = ({ pendingCity = "" }: AddBranchFormProps) => {
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<{ id: number; label: string; value: string }[]>([]);
  const [zones, setZones] = useState<any[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingCities, setLoadingCities] = useState(false);
  const pendingCityRef = useRef<string>("");
  const rawStatesRef = useRef<any[]>([]);
  const rawCitiesRef = useRef<any[]>([]);

  const {
    control,
    formState: { errors },
    clearErrors,
    watch,
    setValue,
  } = useFormContext<BranchFormData>();

  const selectedState = watch("State");
  const selectedCity = watch("City");
const selectedZone = watch("Zone");

// State -> State_Code
useEffect(() => {
  if (!selectedState) {
    setValue("State_Code", "");
    return;
  }
  const match = rawStatesRef.current.find((s: any) => s.Name === selectedState);
  setValue("State_Code", match?.unique_code ?? match?.Code ?? "");
}, [selectedState]);

// City -> City_Code
useEffect(() => {
  if (!selectedCity) {
    setValue("City_Code", "");
    return;
  }
  const match = rawCitiesRef.current.find((c: any) =>
    (typeof c === "object" ? c.City_Name : c) === selectedCity
  );
  const code = typeof match === "object" ? (match.City_Code ?? match.unique_code) : match;
  setValue("City_Code", code ?? "");
}, [selectedCity]);

// Zone -> Zone_Code
useEffect(() => {
  if (!selectedZone) {
    setValue("Zone_Code", "");
    return;
  }
  const match = zones.find((z: any) => z.Name === selectedZone);
  setValue("Zone_Code", match?.unique_code ?? match?.Code ?? match?.Zone_Code ?? "");
}, [selectedZone, zones]);
  useEffect(() => {
    pendingCityRef.current = pendingCity;
  }, [pendingCity]);

  // Load states and zones from master data
  useEffect(() => {
    async function fetchMasterData() {
      try {
        const response = await getMasterDropdownService();
        if (response.status === 200 && response.data) {
          const data = response.data as any;
          const stateList = data.States || data.states || [];
          rawStatesRef.current = stateList;
          setStates(stateList);
          setZones(data.Zones || data.zones || []);
        }
      } catch (error) {
        // console.error("Failed to load master data:", error);
      } finally {
        setLoadingData(false);
      }
    }
    fetchMasterData();
  }, []);

  // Fetch cities when state changes
  useEffect(() => {
    if (!selectedState || loadingData) {
      if (!selectedState) setCities([]);
      return;
    }

    setLoadingCities(true);

    const stateCode = rawStatesRef.current.find((s: any) => s.Name === selectedState)?.unique_code || selectedState;

    getCitiesBasedOnState(stateCode)
      .then(({ data }) => {
        const res = data as any;
        const raw: any[] = Array.isArray(res?.Cities) ? res.Cities : [];
        rawCitiesRef.current = raw;
        const cityList = raw.map((c: any, idx: number) => ({
          id: typeof c === "object" ? (c.id ?? idx) : idx,
          label: typeof c === "object" ? c.City_Name : c,
          value: typeof c === "object" ? c.City_Name : c,
        }));
        setCities(cityList);

        if (pendingCityRef.current) {
  const pending = pendingCityRef.current;
  const matched = raw.find((c: any) =>
    (typeof c === "object" ? (c.City_Code ?? c.unique_code) : c) === pending
  );
  const matchedName = matched ? (typeof matched === "object" ? matched.City_Name : matched) : "";
  setValue("City", matchedName);
  pendingCityRef.current = "";
} else if (!cityList.some((c) => c.value === selectedCity)) {
          setValue("City", "");
        }
      })
      .catch(() => setCities([]))
      .finally(() => setLoadingCities(false));
  }, [selectedState, loadingData]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={control}
          label="Branch Name"
          name="Branch_Name"
          error={errors.Branch_Name?.message}
          required
          onClear={() => clearErrors("Branch_Name")}
          placeholder="Enter Branch Name"
        />

        <Dropdown
          name="State"
          label="State"
          options={states.map((s: any) => ({ id: s.id, label: s.Name, value: s.Name }))}
          control={control}
          error={errors.State?.message}
          required
          isLoading={loadingData}
          placeholder="Select a state"
          onClear={() => clearErrors("State")}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Dropdown
          name="City"
          label="District"
          options={cities}
          control={control}
          error={errors.City?.message}
          required
          disabled={!selectedState}
          isLoading={loadingData || loadingCities}
          placeholder="Select a district"
          onClear={() => clearErrors("City")}
        />

        <Dropdown
          name="Zone"
          label="Zone"
          options={zones.map((z: any) => ({ id: z.id, label: z.Name, value: z.Name }))}
          control={control}
          error={errors.Zone?.message}
          required
          isLoading={loadingData}
          placeholder="Select a zone"
          onClear={() => clearErrors("Zone")}
        />
      </div>

      <FormField
        control={control}
        label="Branch Address"
        name="Branch_Address"
        error={errors.Branch_Address?.message}
        required
        isTextarea
        onClear={() => clearErrors("Branch_Address")}
      />
    </div>
  );
};

AddBranchForm.displayName = "AddBranchForm";
export default AddBranchForm;
